import { notEmpty } from "@product/scmp-sdk";
import { useMeasure } from "@react-hookz/web";
import { useEffect, useState } from "react";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { VerticalItemHeight } from "~/components/article/article-more-on-this-list/consts";
import { useSnapshotState } from "~/lib/hooks";
import type { reduceWhitespaceByAvailableSpaceArticle$key } from "~/queries/__generated__/reduceWhitespaceByAvailableSpaceArticle.graphql";

type Payload = {
  isEnabled?: boolean;
  isShowRightMoreOnThis?: boolean;
  reference: reduceWhitespaceByAvailableSpaceArticle$key;
  specs: {
    adSlotSize: number;
    nativeAdSlotSize: number;
    whitespaceAllowance: number;
  };
  trackers: {
    right: FunctionComponent<{ ref?: React.LegacyRef<HTMLDivElement> }>;
    widgetsBottom?: FunctionComponent<{ ref?: React.LegacyRef<HTMLDivElement> }>;
  };
};

export const useReduceWhitespaceByAvailableSpace = ({
  isEnabled = true,
  isShowRightMoreOnThis = true,
  reference,
  specs: { adSlotSize, nativeAdSlotSize, whitespaceAllowance },
  trackers: { right: RightTracker, widgetsBottom: WidgetsBottomTracker },
}: Payload) => {
  const { moreOnThisArticles } = useFragment(
    graphql`
      fragment reduceWhitespaceByAvailableSpaceArticle on Article {
        moreOnThisArticles {
          __typename
        }
      }
    `,
    reference,
  );

  const [enableMeasure, setEnableMeasure] = useState(true);
  const [widgetBottomAvailableSpaceMeasurement, widgetBottomAvailableSpaceMeasurementReference] =
    useMeasure<HTMLDivElement>(enableMeasure);
  const [rightAvailableSpaceMeasurement, rightAvailableSpaceMeasurementReference] =
    useMeasure<HTMLDivElement>(enableMeasure);

  // Prevent CLS of LHS and RHS when content measurement change due to dynamic content
  const [
    widgetBottomAvailableSpaceMeasurementSnapshot,
    setWidgetBottomAvailableSpaceMeasurementSnapshot,
  ] = useSnapshotState(widgetBottomAvailableSpaceMeasurement);
  const [rightAvailableSpaceMeasurementSnapshot, setRightAvailableSpaceMeasurementSnapshot] =
    useSnapshotState(rightAvailableSpaceMeasurement);

  useEffect(() => {
    if (!isEnabled) return;
    // Recalculate the space when inline paywall is shown or if dynamic content is loaded
    setWidgetBottomAvailableSpaceMeasurementSnapshot(
      widgetBottomAvailableSpaceMeasurementReference.current?.getBoundingClientRect(),
    );
    setRightAvailableSpaceMeasurementSnapshot(
      rightAvailableSpaceMeasurementReference.current?.getBoundingClientRect(),
    );
  }, [
    rightAvailableSpaceMeasurementReference,
    setRightAvailableSpaceMeasurementSnapshot,
    setWidgetBottomAvailableSpaceMeasurementSnapshot,
    widgetBottomAvailableSpaceMeasurementReference,
    isEnabled,
  ]);

  useEffect(() => {
    // Prevent resize observer keep update the state when we already have the value
    if (
      notEmpty(widgetBottomAvailableSpaceMeasurementSnapshot) &&
      notEmpty(rightAvailableSpaceMeasurementSnapshot)
    )
      setEnableMeasure(false);
  }, [widgetBottomAvailableSpaceMeasurementSnapshot, rightAvailableSpaceMeasurementSnapshot]);

  const availableSpaceAfterTopAds =
    (rightAvailableSpaceMeasurementSnapshot?.height ?? 0) +
    whitespaceAllowance -
    adSlotSize -
    nativeAdSlotSize;

  const moreOnThisItemHeight = isShowRightMoreOnThis ? VerticalItemHeight : 0;
  const availableSpaceAfterMustHaveContent =
    availableSpaceAfterTopAds -
    moreOnThisItemHeight * (moreOnThisArticles?.length ?? 0) -
    adSlotSize * 2;

  return {
    reduceWhitespaceByAvailableSpaceSpec: {
      right: {
        shouldRender: rightAvailableSpaceMeasurementSnapshot !== undefined,
        shouldRenderRemaining: availableSpaceAfterMustHaveContent > 0,
        shouldRenderSecondAd: availableSpaceAfterTopAds > 0,
        trackerNode: <RightTracker ref={rightAvailableSpaceMeasurementReference} />,
      },
      widgetsBottom: {
        maximumHeight: widgetBottomAvailableSpaceMeasurementSnapshot?.height
          ? widgetBottomAvailableSpaceMeasurementSnapshot.height + whitespaceAllowance
          : undefined,
        trackerNode: WidgetsBottomTracker ? (
          <WidgetsBottomTracker ref={widgetBottomAvailableSpaceMeasurementReference} />
        ) : null,
      },
    },
  };
};
